import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Swords, Clock, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PvpBattle() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [solution, setSolution] = useState({
    architecture_style: null,
    database: null,
    caching: null,
    messaging: null,
    scaling: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState(300); // 5 minutes

  useEffect(() => {
    loadMatch();
    const interval = setInterval(checkMatchStatus, 3000);
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    if (timer > 0 && !submitted) {
      const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !submitted) {
      handleSubmit();
    }
  }, [timer, submitted]);

  const loadMatch = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/match/${matchId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setMatch(data.match);
      setOpponent(data.opponent);
      setSubmitted(!data.is_your_turn);
      setLoading(false);
    } catch (error) {
      toast.error("Помилка завантаження матчу");
      navigate('/pvp');
    }
  };

  const checkMatchStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/match/${matchId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.match.status === "completed") {
        navigate(`/pvp/result/${matchId}`);
      }
    } catch (error) {
      console.error("Error checking match:", error);
    }
  };

  const handleSelect = (category, value) => {
    setSolution(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async () => {
    if (Object.values(solution).some(v => !v)) {
      toast.error("Оберіть всі компоненти!");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/match/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          match_id: matchId,
          solution: solution
        })
      });
      const data = await response.json();
      
      setSubmitted(true);
      
      if (data.status === "judged") {
        toast.success("Суддя визначив переможця!");
        setTimeout(() => navigate(`/pvp/result/${matchId}`), 2000);
      } else {
        toast.info("Чекаємо на суперника...");
      }
    } catch (error) {
      toast.error("Помилка відправки рішення");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 border-b-2 border-primary animate-spin" />
      </div>
    );
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const categories = [
    {
      key: "architecture_style",
      title: "Архітектурний Стиль",
      options: match.case_scenario.options.architecture_style
    },
    {
      key: "database",
      title: "База Даних",
      options: match.case_scenario.options.database
    },
    {
      key: "caching",
      title: "Кешування",
      options: match.case_scenario.options.caching
    },
    {
      key: "messaging",
      title: "Messaging",
      options: match.case_scenario.options.messaging
    },
    {
      key: "scaling",
      title: "Масштабування",
      options: match.case_scenario.options.scaling
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <Swords className="w-6 h-6 text-orange-500" />
                <span className="text-lg font-bold text-foreground">Battle Arena</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className={`w-5 h-5 ${timer < 60 ? 'text-destructive' : 'text-muted-foreground'}`} />
                <span className={`text-lg font-bold ${timer < 60 ? 'text-destructive' : 'text-foreground'}`}>
                  {formatTime(timer)}
                </span>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center">
              <Avatar className="w-16 h-16 mx-auto mb-3 border-2 border-primary">
                <AvatarImage src={opponent?.picture} />
                <AvatarFallback>{opponent?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <h3 className="text-lg font-bold text-foreground">{opponent?.name}</h3>
              <p className="text-sm text-muted-foreground">Рівень {opponent?.level}</p>
            </div>

            <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 p-6 rounded-xl text-center">
              <Swords className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-foreground">VS</h3>
            </div>

            <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary flex items-center justify-center">
                <span className="text-2xl text-white font-bold">You</span>
              </div>
              <h3 className="text-lg font-bold text-foreground">Ви</h3>
              <p className="text-sm text-muted-foreground">В бою</p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl mb-6"
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">{match.case_scenario.title}</h2>
            <p className="text-lg text-muted-foreground mb-4">{match.case_scenario.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(match.case_scenario.requirements).map(([key, value]) => (
                <div key={key} className="bg-muted/30 p-3 rounded-lg">
                  <p className="text-xs text-muted-foreground uppercase">{key}</p>
                  <p className="text-sm font-bold text-foreground">{value}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {!submitted ? (
            <div className="space-y-6">
              {categories.map((category, idx) => (
                <motion.div
                  key={category.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
                >
                  <h3 className="text-xl font-bold text-foreground mb-4">{category.title}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {category.options.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleSelect(category.key, option)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          solution[category.key] === option
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-border/50 bg-card/30 hover:border-orange-500/50'
                        }`}
                      >
                        <span className="text-foreground font-medium">{option}</span>
                        {solution[category.key] === option && (
                          <CheckCircle className="w-5 h-5 text-orange-500 ml-auto mt-2" />
                        )}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ))}

              <div className="text-center">
                <Button
                  onClick={handleSubmit}
                  data-testid="submit-solution-button"
                  size="lg"
                  disabled={Object.values(solution).some(v => !v)}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-14 px-10 text-lg rounded-xl font-bold"
                >
                  Підтвердити Рішення
                </Button>
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card/50 backdrop-blur-sm border border-orange-500/30 p-12 rounded-2xl text-center"
            >
              <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
              <h3 className="text-3xl font-bold text-foreground mb-4">Чекаємо на суперника...</h3>
              <p className="text-lg text-muted-foreground">
                Ваше рішення прийнято. AI суддя визначить переможця після того, як суперник зробить свій вибір.
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
